import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { ConfigEntryDTO, ConfigLayerDTO, ConfigScope, EnvironmentConfigDTO, ProjectDTO } from '@devrobot/shared';
import { ChevronDown, ChevronRight, Eye, FileUp, Lock, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { api, seg } from '../../../api/client';
import { qk, useApiMutation, useEnvConfig, useEnvironments, useProjectTargets, useVpsList } from '../../../api/queries';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { CopyButton } from '../../../components/CopyButton';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { Section } from '../../../components/PageHeader';
import { RelativeTime } from '../../../components/RelativeTime';
import { SkeletonTable } from '../../../components/Skeleton';
import { StatusBadge } from '../../../components/StatusBadge';
import { useToast } from '../../../components/Toaster';
import { listTransition, useCountUp } from '../../../kit/hooks';
import { haptic } from '../../../kit/native';
import { ImportModal } from './ImportModal';
import { PrivateFiles } from './PrivateFiles';
import { describeScope, scopeForLayer, scopeKey, scopeQuery } from './scope';
import { SetValueModal } from './SetValueModal';

/** CSS-ident-safe view-transition name for a variable row. */
const vtName = (name: string, suffix = '') => ({ '--vt': `cfg-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}${suffix}` }) as React.CSSProperties;

function StatNumber({ value, className = '' }: { value: number; className?: string }) {
  const ref = useCountUp<HTMLDivElement>(value);
  return (
    <div ref={ref} className={`text-2xl font-semibold tabular-nums ${className}`}>
      {value}
    </div>
  );
}

interface Revealed {
  value: string;
  label: string;
}

export function ConfigTab({ project }: { project: ProjectDTO }) {
  const key = project.key;
  const [params, setParams] = useSearchParams();
  const envs = useEnvironments(key);
  const env = params.get('env') ?? envs.data?.find((e) => e.name === 'development')?.name ?? envs.data?.[0]?.name;
  const cfg = useEnvConfig(key, env);

  const selectEnv = (name: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'config');
    next.set('env', name);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-base-content/65" id="env-picker-label">
          Environment
        </span>
        <div className="-mx-1 overflow-x-auto px-1">
          <div role="tablist" aria-labelledby="env-picker-label" className="tabs tabs-box tabs-sm min-w-max border border-hairline">
            {(envs.data ?? []).map((e) => (
              <button key={e.id} type="button" role="tab" aria-selected={e.name === env} className={`tab gap-1 ${e.name === env ? 'tab-active' : ''}`} onClick={() => selectEnv(e.name)}>
                {e.protected ? <Lock className="size-3" aria-label="protected" /> : null}
                {e.name}
                {e.completeness.missing.length + e.completeness.missingFiles.length > 0 ? (
                  <span className="badge badge-soft badge-error badge-xs tabular-nums" title="missing">
                    {e.completeness.missing.length + e.completeness.missingFiles.length}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>
      {envs.isError ? <ErrorAlert error={envs.error} /> : null}
      {cfg.isPending && env ? <SkeletonTable /> : null}
      {cfg.isError ? <ErrorAlert error={cfg.error} onRetry={() => void cfg.refetch()} /> : null}
      {cfg.data ? <EnvironmentConfig projectKey={key} cfg={cfg.data} /> : null}
    </div>
  );
}

function Completeness({ cfg }: { cfg: EnvironmentConfigDTO }) {
  const c = cfg.completeness;
  const missing = c.missing.length + c.missingFiles.length;
  return (
    <div className="space-y-3">
      <div className="dr-stagger grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: 'Variables', value: c.total },
          { label: 'Required', value: c.required },
          { label: 'Missing', value: missing, cls: missing ? 'dr-ink-error' : 'dr-ink-success' },
          { label: 'Intentionally empty', value: c.empty.length },
        ].map((s) => (
          <div key={s.label} className="rounded-field bg-base-200/60 p-3">
            <div className="text-xs tracking-wide text-base-content/55 uppercase">{s.label}</div>
            <StatNumber value={s.value} className={s.cls ?? ''} />
          </div>
        ))}
      </div>
      {missing > 0 ? (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          <div>
            Deployments to <strong>{cfg.environment}</strong> are blocked until these are set:{' '}
            {[...c.missing, ...c.missingFiles.map((f) => `file ${f}`)].map((m, i) => (
              <Fragment key={m}>
                {i > 0 ? ', ' : ''}
                <span className="font-mono">{m}</span>
              </Fragment>
            ))}
          </div>
        </div>
      ) : null}
      <p className="text-sm text-base-content/65">
        Saving a value does not change running applications. Targets show <span className="badge badge-soft badge-warning badge-xs">configuration pending</span> until someone applies it from the target page.
      </p>
    </div>
  );
}

function EnvironmentConfig({ projectKey, cfg }: { projectKey: string; cfg: EnvironmentConfigDTO }) {
  const toast = useToast();
  const vpsList = useVpsList();
  const targets = useProjectTargets(projectKey);
  const env = cfg.environment;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [setting, setSetting] = useState<{ entry?: ConfigEntryDTO; scope?: ConfigScope } | null>(null);
  const [importing, setImporting] = useState(false);
  const [unsetting, setUnsetting] = useState<{ name: string; scope: ConfigScope } | null>(null);
  const [revealing, setRevealing] = useState<{ name: string; scope: ConfigScope } | null>(null);
  const [revealed, setRevealed] = useState<Record<string, Revealed>>({});
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  // The list re-flows inside a list transition shortly after typing stops, so rows glide into place.
  useEffect(() => {
    if (query === appliedQuery) return;
    const t = setTimeout(() => listTransition(() => setAppliedQuery(query)), 160);
    return () => clearTimeout(t);
  }, [query, appliedQuery]);
  const needle = appliedQuery.trim().toLowerCase();
  const entries = needle ? cfg.entries.filter((e) => e.name.toLowerCase().includes(needle) || (e.description ?? '').toLowerCase().includes(needle)) : cfg.entries;

  const base = `/api/v1/projects/${seg(projectKey)}/environments/${seg(env)}/config`;
  const invalidate = [qk.config(projectKey, env), qk.environments(projectKey), qk.targets(projectKey), ['target']];
  const unset = useApiMutation((v: { name: string; scope: ConfigScope }) => api.delete(`${base}/${seg(v.name)}`, { query: scopeQuery(v.scope) }), { invalidate });
  const reveal = useApiMutation((v: { name: string; scope: ConfigScope }) => api.post<{ value: string; version: number; label: string }>(`${base}/${seg(v.name)}/reveal`, { scope: v.scope }));

  const vps = vpsList.data ?? [];
  const tg = targets.data ?? [];
  const can = cfg.can;
  const rKey = (name: string, scope: ConfigScope) => `${name}|${scopeKey(scope)}`;

  const toggle = (name: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });

  const layerActions = (entry: ConfigEntryDTO, layer: ConfigLayerDTO) => {
    const scope = scopeForLayer(layer, vps, tg);
    if (!scope) return null;
    const rk = rKey(entry.name, scope);
    return (
      <span className="flex justify-end gap-1">
        {entry.secret && can.revealSecrets && layer.state === 'set' && !revealed[rk] ? (
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setRevealing({ name: entry.name, scope })}>
            <Eye className="size-3.5" aria-hidden="true" /> Reveal
          </button>
        ) : null}
        {(can.writeConfig || scope.kind === 'personal') ? (
          <>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => setSetting({ entry, scope })} aria-label={`Replace ${entry.name} at ${layer.label}`}>
              <Pencil className="size-3.5" aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-ghost btn-xs dr-ink-error" onClick={() => setUnsetting({ name: entry.name, scope })} aria-label={`Unset ${entry.name} at ${layer.label}`}>
              <Trash2 className="size-3.5" aria-hidden="true" />
            </button>
          </>
        ) : null}
      </span>
    );
  };

  const valueCell = (entry: ConfigEntryDTO) => {
    const winnerLayer = entry.source ? (entry.layers.find((l) => l.layer === entry.source!.layer && l.label === entry.source!.label) ?? { layer: entry.source.layer, label: entry.source.label }) : null;
    const winnerScope = winnerLayer ? scopeForLayer(winnerLayer, vps, tg) : null;
    const shown = winnerScope ? revealed[rKey(entry.name, winnerScope)] : undefined;
    if (shown) {
      return (
        <span className="flex items-center gap-1">
          <code className="break-anywhere rounded bg-warning/15 px-1 font-mono text-xs">{shown.value || '(empty)'}</code>
          <CopyButton value={shown.value} label={`Copy ${entry.name}`} />
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setRevealed(({ [rKey(entry.name, winnerScope!)]: _, ...rest }) => rest)}>
            Hide
          </button>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1">
        {entry.displayValue !== undefined ? <code className="break-anywhere font-mono text-xs text-base-content/80">{entry.displayValue === '' ? '(empty)' : entry.displayValue}</code> : <span className="text-base-content/40">—</span>}
        {entry.secret && can.revealSecrets && winnerScope && entry.state === 'set' ? (
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setRevealing({ name: entry.name, scope: winnerScope })} aria-label={`Reveal ${entry.name}`}>
            <Eye className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </span>
    );
  };

  return (
    <>
      <Section title={`Configuration of ${env}`} description={cfg.manifestRef ? `Declared by the manifest at ${cfg.manifestRef.slice(0, 12)}.` : 'No manifest registered yet: only undeclared values are listed.'}>
        <Completeness cfg={cfg} />
        {can.writeConfig ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-sm" onClick={() => setSetting({})}>
              <Plus className="size-4" aria-hidden="true" /> Add variable
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setImporting(true)}>
              <FileUp className="size-4" aria-hidden="true" /> Import .env
            </button>
          </div>
        ) : (
          <div role="note" className="alert alert-info alert-soft text-sm">
            You can view names and completeness. Changing {cfg.protected ? 'protected-environment ' : ''}configuration needs the GitLab {cfg.protected ? 'Maintainer' : 'Developer'} role.
            {env === 'development' ? ' You can still set personal overrides for local development from a value’s details.' : ''}
          </div>
        )}
        {cfg.entries.length > 5 || query ? (
          <label className="input input-sm w-full lg:w-72">
            <Search className="size-4 opacity-50" aria-hidden="true" />
            <input type="search" data-page-search value={query} onChange={(ev) => setQuery(ev.target.value)} placeholder="Filter variables" aria-label="Filter variables" />
            <kbd className="kbd kbd-sm max-sm:hidden">/</kbd>
          </label>
        ) : null}
        <div className="-mx-4 overflow-x-auto border-y border-hairline sm:mx-0 sm:rounded-field sm:border">
          <table className="table table-sm">
            <thead>
              <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                <th>
                  <span className="sr-only">Expand</span>
                </th>
                <th>Variable</th>
                <th>State</th>
                <th>Value</th>
                <th className="max-lg:hidden">Source</th>
                <th className="max-md:hidden">Services</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {cfg.entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-base-content/60">
                    No variables are declared or set for {env}.
                  </td>
                </tr>
              ) : null}
              {cfg.entries.length > 0 && entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-base-content/60">
                    No variable matches “{appliedQuery.trim()}”.{' '}
                    <button type="button" className="link" onClick={() => (setQuery(''), listTransition(() => setAppliedQuery('')))}>
                      Clear filter
                    </button>
                  </td>
                </tr>
              ) : null}
              {entries.map((e) => {
                const open = expanded.has(e.name);
                const state = e.state === 'missing' && !e.required ? 'missing-optional' : e.state;
                return (
                  <Fragment key={e.name}>
                    <tr data-vt style={vtName(e.name)} className={`hover:bg-base-200/50 ${e.applicable ? '' : 'opacity-60'}`}>
                      <td className="w-8">
                        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={() => toggle(e.name)} aria-expanded={open} aria-label={`${open ? 'Hide' : 'Show'} layers of ${e.name}`}>
                          {open ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
                        </button>
                      </td>
                      <td className="min-w-44">
                        <div className="flex items-center gap-1.5 font-mono text-sm font-medium">
                          {e.secret ? <Lock className="size-3.5 shrink-0 text-base-content/60" aria-label="secret" /> : null}
                          {e.name}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {e.required ? <span className="badge badge-ghost badge-xs">required</span> : null}
                          {e.secret ? <span className="badge badge-ghost badge-xs">secret</span> : null}
                          {e.buildTime ? <span className="badge badge-ghost badge-xs" title="Passed to the GitLab CI build, separate from runtime secrets">build-time</span> : null}
                          {!e.declared ? <span className="badge badge-ghost badge-xs">not in manifest</span> : null}
                        </div>
                        {e.description ? <div className="max-w-xs text-xs text-base-content/60">{e.description}</div> : null}
                      </td>
                      <td>
                        <StatusBadge kind="config" value={state} size="xs" />
                      </td>
                      <td className="max-w-xs">{valueCell(e)}</td>
                      <td className="text-xs max-lg:hidden">{e.source ? `${e.source.label} · v${e.source.version}` : '—'}</td>
                      <td className="text-xs max-md:hidden">{e.services.join(', ') || '—'}</td>
                      <td className="text-right">
                        {can.writeConfig ? (
                          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setSetting({ entry: e })}>
                            {e.state === 'missing' ? 'Set' : 'Replace'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {open ? (
                      <tr data-vt style={vtName(e.name, '-layers')} className="bg-base-200/50">
                        <td />
                        <td colSpan={6} className="animate-rise">
                          <p className="mb-2 text-xs text-base-content/65">Layers from lowest to highest precedence; the last set layer wins. Secret values are masked.</p>
                          {e.layers.length === 0 ? (
                            <p className="text-sm text-base-content/60">No value in any layer.</p>
                          ) : (
                            <table className="table table-xs rounded-field bg-base-100">
                              <thead>
                                <tr>
                                  <th>Layer</th>
                                  <th>State</th>
                                  <th>Value</th>
                                  <th>Version</th>
                                  <th>Updated</th>
                                  <th>
                                    <span className="sr-only">Actions</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {e.layers.map((l, i) => {
                                  const scope = scopeForLayer(l, vps, tg);
                                  const shown = scope ? revealed[rKey(e.name, scope)] : undefined;
                                  const winner = e.source?.layer === l.layer && e.source?.label === l.label;
                                  return (
                                    <tr key={`${l.layer}-${l.label}-${i}`}>
                                      <td>
                                        {l.label} {winner ? <span className="badge badge-primary badge-soft badge-xs">in effect</span> : null}
                                      </td>
                                      <td>{l.state === 'empty' ? 'empty (intentional)' : 'set'}</td>
                                      <td className="max-w-xs">
                                        <code className="break-anywhere font-mono">{shown ? shown.value : l.value !== undefined ? l.value || '(empty)' : e.secret ? '••••••••' : '—'}</code>
                                      </td>
                                      <td>v{l.version}</td>
                                      <td>
                                        <RelativeTime value={l.updatedAt} fallback="—" />
                                        {l.updatedBy ? ` by ${l.updatedBy}` : ''}
                                      </td>
                                      <td>{layerActions(e, l)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          {env === 'development' ? (
                            <button type="button" className="btn btn-ghost btn-xs mt-2" onClick={() => setSetting({ entry: e, scope: { kind: 'personal' } })}>
                              <Plus className="size-3" aria-hidden="true" /> Personal override for local development
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {cfg.bindings.length > 0 ? (
          <p className="text-xs text-base-content/65">
            Shared bindings:{' '}
            {cfg.bindings.map((b, i) => (
              <Fragment key={b.variable}>
                {i > 0 ? ', ' : ''}
                <span className="font-mono">{b.variable}</span> →{' '}
                <Link className="link" to="/shared">
                  {b.sharedValueName}
                </Link>{' '}
                v{b.version}
              </Fragment>
            ))}
          </p>
        ) : null}
      </Section>

      <Section title="Private files" description="Files delivered into containers at a declared path with declared permissions; their content never appears in the repository, images or logs.">
        <PrivateFiles projectKey={projectKey} cfg={cfg} />
      </Section>

      {setting ? <SetValueModal projectKey={projectKey} cfg={cfg} entry={setting.entry} initialScope={setting.scope} vpsList={vps} targets={tg} onClose={() => setSetting(null)} /> : null}
      {importing ? <ImportModal projectKey={projectKey} cfg={cfg} vpsList={vps} targets={tg} onClose={() => setImporting(false)} /> : null}
      {unsetting ? (
        <ConfirmDialog
          title={`Unset ${unsetting.name}?`}
          confirmLabel="Unset value"
          tone="error"
          busy={unset.isPending}
          error={unset.error}
          onCancel={() => (setUnsetting(null), unset.reset())}
          onConfirm={() =>
            unset.mutate(unsetting, {
              onSuccess: () => {
                toast.success(`${unsetting.name} unset`, { description: `Removed at ${describeScope(unsetting.scope, env, vps, tg)}.` });
                setUnsetting(null);
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>
            Removes the value stored at <strong>{describeScope(unsetting.scope, env, vps, tg)}</strong>. A lower-precedence layer takes effect if there is one; otherwise the variable becomes missing
            {cfg.entries.find((x) => x.name === unsetting.name)?.required ? ', which blocks deployments because it is required' : ''}. Running applications are not changed until configuration is applied.
          </p>
        </ConfirmDialog>
      ) : null}
      {revealing ? (
        <ConfirmDialog
          title={`Reveal ${revealing.name}?`}
          confirmLabel="Reveal value"
          tone="warning"
          busy={reveal.isPending}
          error={reveal.error}
          onCancel={() => (setRevealing(null), reveal.reset())}
          onConfirm={() =>
            reveal.mutate(revealing, {
              onSuccess: (r) => {
                setRevealed((m) => ({ ...m, [rKey(revealing.name, revealing.scope)]: { value: r.value, label: r.label } }));
                setRevealing(null);
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>
            The plaintext value at <strong>{describeScope(revealing.scope, env, vps, tg)}</strong> is shown on this page until you hide it or leave. The reveal is recorded in the audit log with your name
            {cfg.protected ? ' (protected environment)' : ''}.
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
